/**
 * 검색 결과 Mock Store
 * OpenMetadata 검색 API를 목업으로 처리합니다.
 */
import { ALL_ICEBERG_DATA } from '@/shared/api/demo/data/icebergData';
import type {
  DomainAggregationResponse,
  OpenMetadataAggregationBucket,
  OpenMetadataHit,
  OpenMetadataResponse,
  OpenMetadataSource,
  OpenMetadataTag,
} from '@/shared/api/openmetadata/types';

import { ALL_GLOSSARY_TERMS } from './glossaryMockStore';
import { getTagsMockData } from './tagMockStore';

type MockAggregation = {
  doc_count_error_upper_bound?: number;
  sum_other_doc_count?: number;
  buckets: OpenMetadataAggregationBucket[];
};

type MockOpenMetadataAggregations = Record<string, MockAggregation | undefined>;

type OpenMetadataSourceWithTerms = OpenMetadataHit['_source'] & {
  terms?: Array<{
    fullyQualifiedName: string;
    name: string;
    displayName?: string;
  }>;
};

type FilterState = {
  dataSource?: string[];
  assetType?: string[];
  domain?: string[];
  owner?: string[];
  tier?: string[];
  tags?: string[];
  certification?: string[];
  quickFilter?: {
    serviceType?: string;
    serviceName?: string;
    databaseName?: string;
    schemaName?: string;
    entityType?: string;
  };
};

const createGlossaryTermSearchHits = (): OpenMetadataHit[] =>
  ALL_GLOSSARY_TERMS.map((term, index) => ({
    _index: 'glossary_search_index',
    _id: term.id,
    _score: 1,
    _source: {
      id: term.id,
      name: term.name,
      displayName: term.displayName || term.name,
      description: term.description,
      fullyQualifiedName: term.fullyQualifiedName,
      version: term.version || 0.1,
      updatedAt: term.updatedAt || Date.now() - index * 1000,
      updatedBy: term.updatedBy || 'admin',
      entityType: 'glossaryTerm',
      owners: (term.owners || []).map((owner) => ({
        id: owner.id,
        deleted: owner.deleted ?? false,
        displayName: owner.displayName || owner.name,
        name: owner.name,
        type: owner.type,
        fullyQualifiedName: owner.fullyQualifiedName || owner.name,
      })),
      tags: (term.tags || []).map(
        (tag): OpenMetadataTag => ({
          ...tag,
          source: tag.source as OpenMetadataTag['source'],
        })
      ),
      domains: term.domains?.map((domain) => ({
        id: domain.id,
        name: domain.name,
        fullyQualifiedName: domain.fullyQualifiedName,
        displayName: domain.displayName,
      })),
      deleted: term.deleted || false,
      glossary: term.glossary
        ? {
            id: term.glossary.id,
            type: term.glossary.type,
            name: term.glossary.name,
            fullyQualifiedName: term.glossary.fullyQualifiedName,
            displayName: term.glossary.displayName,
            description: '',
            deleted: term.glossary.deleted,
          }
        : undefined,
      status: term.status || 'Approved',
      references: term.references || [],
      parent: term.parent
        ? {
            deleted: term.parent.deleted,
            displayName: term.parent.displayName,
            name: term.parent.name,
            description: '',
            id: term.parent.id,
            type: term.parent.type,
            fullyQualifiedName: term.parent.fullyQualifiedName,
          }
        : undefined,
    } satisfies OpenMetadataSource,
  }));

const createTagSearchHits = (): OpenMetadataHit[] => {
  const tags = getTagsMockData().data;

  return tags.map((tag, index) => ({
    _index: 'tag_search_index',
    _id: tag.id,
    _score: 1,
    _source: {
      id: tag.id,
      name: tag.name,
      displayName: tag.displayName || tag.name,
      description: tag.description,
      fullyQualifiedName: tag.fullyQualifiedName,
      version: tag.version || 0.1,
      updatedAt: tag.updatedAt || Date.now() - index * 1000,
      updatedBy: tag.updatedBy || 'admin',
      entityType: 'tag',
      owners: [],
      tags: [],
      deleted: tag.deleted || false,
      classification: tag.classification
        ? {
            id: tag.classification.id,
            type: tag.classification.type,
            name: tag.classification.name,
            fullyQualifiedName: tag.classification.fullyQualifiedName,
            description: tag.classification.description,
            displayName: tag.classification.displayName,
            deleted: tag.classification.deleted,
          }
        : undefined,
    } satisfies OpenMetadataSource,
  }));
};

const createInitialMockData = (): OpenMetadataHit[] => [
  ...assignDomainsToAssets([...ALL_ICEBERG_DATA]),
  ...createGlossaryTermSearchHits(),
  ...createTagSearchHits(),
];

const LGES_PROCESS_TAG_ROTATION = [
  'LGES Process.Cutting',
  'LGES Process.Stacking',
  'LGES Process.CellFormation',
  'LGES Process.ElectrodeAssembly',
  'LGES Process.DiagnosticAnalysis',
  'LGES Process.FeaturePreparation',
] as const;

type LgesProcessTagFqn = (typeof LGES_PROCESS_TAG_ROTATION)[number];

const LGES_PROCESS_TAG_META: Record<LgesProcessTagFqn, { name: string; displayName: string; description: string }> = {
  'LGES Process.Cutting': {
    name: 'Cutting',
    displayName: '커팅 공정',
    description: '슬리팅·커팅 단계 분류 데이터',
  },
  'LGES Process.Stacking': {
    name: 'Stacking',
    displayName: '적층·스택킹 공정',
    description: '스택 적층 단계 분류 데이터',
  },
  'LGES Process.CellFormation': {
    name: 'CellFormation',
    displayName: '셀 형성 공정',
    description: '셀 형성(포밍 등) 단계 데이터',
  },
  'LGES Process.ElectrodeAssembly': {
    name: 'ElectrodeAssembly',
    displayName: '전극·조립 공정',
    description: '전극 및 조립 공정 데이터',
  },
  'LGES Process.DiagnosticAnalysis': {
    name: 'DiagnosticAnalysis',
    displayName: '진단·분석 (ECMS)',
    description: 'ECMS 진단·분석 단계 데이터',
  },
  'LGES Process.FeaturePreparation': {
    name: 'FeaturePreparation',
    displayName: '피처 전처리',
    description: '피처 생성·전처리 단계 데이터',
  },
};

const createLgesProcessClassificationTag = (fqn: LgesProcessTagFqn): OpenMetadataTag => {
  const m = LGES_PROCESS_TAG_META[fqn];
  return {
    tagFQN: fqn,
    name: m.name,
    displayName: m.displayName,
    labelType: 'Manual',
    description: m.description,
    source: 'Classification',
    state: 'Confirmed',
  };
};

/**
 * 자산에 도메인, 태그, 용어를 할당하는 함수
 * 테이블들을 각 도메인에 골고루 배분하고, 관련 태그와 용어도 할당합니다.
 */
const assignDomainsToAssets = (hits: OpenMetadataHit[]): OpenMetadataHit[] => {
  const domainList = [
    {
      id: 'c32d32c4-c01c-415d-8c56-547dd25f9652',
      type: 'domain',
      name: 'Turning',
      fullyQualifiedName: 'Manufacturing.Site_A.CNC_Cell.Turning',
      displayName: '터닝 공정',
    },
    {
      id: '1251bff6-dfd1-4d5c-9844-691a33f15b5a',
      type: 'domain',
      name: 'Milling',
      fullyQualifiedName: 'Manufacturing.Site_B.MCT_Cell.Milling',
      displayName: '밀링 공정',
    },
    {
      id: '743ccea1-10e4-44a2-b9ba-5deda23f4108',
      type: 'domain',
      name: 'Dimensional',
      fullyQualifiedName: 'Quality.Inspection.Dimensional',
      displayName: '치수 검사',
    },
    {
      id: 'd6c0419f-fc91-4956-a1b8-68e6f210115a',
      type: 'domain',
      name: 'Visual',
      fullyQualifiedName: 'Quality.Inspection.Visual',
      displayName: '외관 검사',
    },
    {
      id: '63b02c08-b57c-4fab-8645-591dfa03de0c',
      type: 'domain',
      name: 'Defect_Analysis',
      fullyQualifiedName: 'Quality.Defect_Analysis',
      displayName: '불량 분석',
    },
    {
      id: '59d47ee9-1c87-4f68-a31c-b18c99850e93',
      type: 'domain',
      name: 'Durability',
      fullyQualifiedName: 'Quality.Testing.Durability',
      displayName: '내구 시험',
    },
    {
      id: 'a20e23d2-924f-4d9c-84dc-1045a603b2bf',
      type: 'domain',
      name: 'Environmental',
      fullyQualifiedName: 'Quality.Testing.Environmental',
      displayName: '환경 시험',
    },
    {
      id: '3e93335b-3b04-4238-a24d-ffece1cec098',
      type: 'domain',
      name: 'Functional',
      fullyQualifiedName: 'Quality.Testing.Functional',
      displayName: '기능 시험',
    },
    {
      id: '795e251b-c697-42b9-9166-794a3a944254',
      type: 'domain',
      name: 'Operations',
      fullyQualifiedName: 'Operations',
      displayName: '운영',
    },
    {
      id: 'e7a1f2d3-c4b5-4678-9abc-def012340001',
      type: 'domain',
      name: 'LGES',
      fullyQualifiedName: 'LGES',
      displayName: 'LGES',
    },
  ];

  const LGES_ROOT_DOMAIN_INDEX = domainList.findIndex((d) => d.fullyQualifiedName === 'LGES');

  return hits.map((hit, index) => {
    const source = hit._source;
    const domains: Array<{ id: string; type: string; name: string; fullyQualifiedName: string; displayName: string }> =
      [];

    const tableName = source.name.toLowerCase();
    const displayName = (source.displayName || '').toLowerCase();
    const description = (source.description || '').toLowerCase();

    // 키워드 기반 우선 매칭
    let matched = false;

    // LGES · ECMS 등 (데모에서는 루트 도메인 LGES만 검색 결과에 노출 — 하위 도메인은 도메인 트리에서만 사용)
    if (
      tableName.includes('lges') ||
      displayName.includes('lges') ||
      tableName.includes('lg_es') ||
      tableName.includes('ecms') ||
      tableName.includes('kmp') ||
      tableName.includes('gsp') ||
      tableName.includes('hanwha') ||
      displayName.includes('ecms') ||
      description.includes('ecms') ||
      tableName.includes('diagnosis') ||
      displayName.includes('diagnosis') ||
      tableName.includes('diagnostic') ||
      description.includes('diagnosis')
    ) {
      domains.push(domainList[LGES_ROOT_DOMAIN_INDEX]);
      matched = true;
    } else if (
      tableName.includes('turning') ||
      displayName.includes('turning') ||
      tableName.includes('lathe') ||
      description.includes('turning') ||
      description.includes('lathe')
    ) {
      domains.push(domainList[0]);
      matched = true;
    } else if (
      tableName.includes('milling') ||
      displayName.includes('milling') ||
      tableName.includes('mill') ||
      description.includes('milling') ||
      description.includes('mill')
    ) {
      domains.push(domainList[1]);
      matched = true;
    }
    // 품질 관련 키워드
    else if (
      tableName.includes('dimension') ||
      displayName.includes('dimension') ||
      tableName.includes('measurement') ||
      tableName.includes('cmm') ||
      description.includes('치수') ||
      description.includes('측정')
    ) {
      domains.push(domainList[2]);
      matched = true;
    } else if (
      tableName.includes('visual') ||
      displayName.includes('visual') ||
      tableName.includes('appearance') ||
      tableName.includes('scratch') ||
      description.includes('외관') ||
      description.includes('스크래치')
    ) {
      domains.push(domainList[3]);
      matched = true;
    } else if (
      tableName.includes('defect') ||
      displayName.includes('defect') ||
      tableName.includes('fault') ||
      tableName.includes('failure') ||
      description.includes('불량') ||
      description.includes('결함')
    ) {
      domains.push(domainList[4]);
      matched = true;
    } else if (
      tableName.includes('durability') ||
      displayName.includes('durability') ||
      tableName.includes('endurance') ||
      tableName.includes('fatigue') ||
      description.includes('내구') ||
      description.includes('수명')
    ) {
      domains.push(domainList[5]);
      matched = true;
    } else if (
      tableName.includes('environmental') ||
      displayName.includes('environmental') ||
      tableName.includes('temperature') ||
      tableName.includes('humidity') ||
      tableName.includes('thermal') ||
      description.includes('환경') ||
      description.includes('고온') ||
      description.includes('저온')
    ) {
      domains.push(domainList[6]);
      matched = true;
    } else if (
      tableName.includes('functional') ||
      displayName.includes('functional') ||
      tableName.includes('brake') ||
      tableName.includes('steering') ||
      description.includes('기능') ||
      description.includes('브레이크')
    ) {
      domains.push(domainList[7]);
      matched = true;
    } else if (
      tableName.includes('operation') ||
      displayName.includes('operation') ||
      tableName.includes('maintenance') ||
      tableName.includes('monitor') ||
      description.includes('운영') ||
      description.includes('유지보수')
    ) {
      domains.push(domainList[8]);
      matched = true;
    }

    // 키워드 매칭이 안된 경우 골고루 배분 (라운드 로빈 방식)
    if (!matched) {
      const domainIndex = index % domainList.length;
      domains.push(domainList[domainIndex]);
    }

    // 태그 할당 로직
    const tags: OpenMetadataTag[] = [];

    // Certification 태그 할당 (품질이 높은 데이터에 Gold, 일반적인 데이터에 Bronze)
    const certificationIndex = index % 3;
    if (certificationIndex === 0) {
      tags.push({
        tagFQN: 'Certification.Gold',
        name: 'Gold',
        displayName: 'Gold',
        labelType: 'Derived',
        description: 'Gold certified Data Asset.',
        source: 'Classification',
        state: 'Confirmed',
      });
    } else if (certificationIndex === 1) {
      tags.push({
        tagFQN: 'Certification.Silver',
        name: 'Silver',
        displayName: 'Silver',
        labelType: 'Derived',
        description: 'Silver certified Data Asset.',
        source: 'Classification',
        state: 'Confirmed',
      });
    } else {
      tags.push({
        tagFQN: 'Certification.Bronze',
        name: 'Bronze',
        displayName: 'Bronze',
        labelType: 'Derived',
        description: 'Bronze certified Data Asset.',
        source: 'Classification',
        state: 'Confirmed',
      });
    }

    // Equipment Type 태그 할당 (키워드 기반)
    let hasCNCLathe = false;
    if (
      tableName.includes('turning') ||
      tableName.includes('lathe') ||
      displayName.includes('turning') ||
      description.includes('선반')
    ) {
      tags.push({
        tagFQN: 'Equipment Type.CNC_Lathe',
        name: 'CNC_Lathe',
        displayName: 'CNC Lathe',
        labelType: 'Manual',
        description: '선반 기반 CNC 설비에서 수집된 데이터',
        source: 'Classification',
        state: 'Confirmed',
      });
      hasCNCLathe = true;
    }

    if (
      tableName.includes('furnace') ||
      displayName.includes('furnace') ||
      tableName.includes('thermal') ||
      description.includes('가열')
    ) {
      tags.push({
        tagFQN: 'Equipment Type.Furnace',
        name: 'Furnace',
        displayName: 'Furnace',
        labelType: 'Manual',
        description: '가열로 설비에서 수집된 데이터',
        source: 'Classification',
        state: 'Confirmed',
      });
    }

    const hasLgesDomain = domains.some((d) => d.fullyQualifiedName === 'LGES' || d.name === 'LGES');
    if (hasLgesDomain) {
      const blob = `${tableName} ${displayName} ${description}`;
      let chosenFqn: LgesProcessTagFqn | undefined;
      if (blob.includes('cutting') || blob.includes('slit') || blob.includes('슬리팅') || blob.includes('커팅')) {
        chosenFqn = 'LGES Process.Cutting';
      } else if (blob.includes('stack') || blob.includes('적층') || blob.includes('스택')) {
        chosenFqn = 'LGES Process.Stacking';
      } else if (
        blob.includes('formation') ||
        blob.includes('forming') ||
        blob.includes('형성') ||
        blob.includes('포밍')
      ) {
        chosenFqn = 'LGES Process.CellFormation';
      } else if (
        blob.includes('electrode') ||
        blob.includes('cathode') ||
        blob.includes('anode') ||
        blob.includes('전극') ||
        blob.includes('조립')
      ) {
        chosenFqn = 'LGES Process.ElectrodeAssembly';
      } else if (
        blob.includes('diagnosis') ||
        blob.includes('diagnostic') ||
        blob.includes('ecms') ||
        blob.includes('진단')
      ) {
        chosenFqn = 'LGES Process.DiagnosticAnalysis';
      } else if (
        blob.includes('feature') ||
        blob.includes('preprocess') ||
        blob.includes('피처') ||
        blob.includes('preprocessed')
      ) {
        chosenFqn = 'LGES Process.FeaturePreparation';
      }

      tags.push(
        createLgesProcessClassificationTag(
          chosenFqn ?? LGES_PROCESS_TAG_ROTATION[index % LGES_PROCESS_TAG_ROTATION.length]
        )
      );
    }

    // 용어(Glossary Term) 할당 로직
    const termTags: OpenMetadataTag[] = [];

    // 제조 관련 용어 할당
    // CNC 관련 데이터에 CNC 용어 할당 (CNC_Lathe 태그가 있는 경우 포함)
    if (
      hasCNCLathe ||
      tableName.includes('cnc') ||
      tableName.includes('lathe') ||
      displayName.toLowerCase().includes('cnc') ||
      description.includes('CNC') ||
      description.includes('수치제어')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.CNC',
        name: 'CNC',
        displayName: 'CNC (Computer Numerical Control)',
        labelType: 'Manual',
        description: '컴퓨터 수치 제어 시스템',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('turning') ||
      displayName.includes('turning') ||
      description.includes('선삭') ||
      description.includes('선반')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Turning',
        name: 'Turning',
        displayName: '터닝',
        labelType: 'Manual',
        description: '터닝 (선삭) 공정',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('milling') ||
      displayName.includes('milling') ||
      description.includes('밀링') ||
      description.includes('프레이즈')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Milling',
        name: 'Milling',
        displayName: '밀링',
        labelType: 'Manual',
        description: '밀링 (프레이즈) 공정',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('spindle') ||
      displayName.includes('spindle') ||
      tableName.includes('rpm') ||
      description.includes('주축') ||
      description.includes('회전')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.SpindleSpeed',
        name: 'SpindleSpeed',
        displayName: '주축 회전 속도',
        labelType: 'Manual',
        description: '주축 회전 속도 (RPM)',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('feed') ||
      displayName.includes('feed') ||
      description.includes('이송') ||
      description.includes('feed')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.FeedRate',
        name: 'FeedRate',
        displayName: '이송 속도',
        labelType: 'Manual',
        description: '이송 속도',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('vibration') ||
      displayName.includes('vibration') ||
      description.includes('진동') ||
      tableName.includes('vib')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Vibration',
        name: 'Vibration',
        displayName: '진동',
        labelType: 'Manual',
        description: '설비 진동 데이터',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('temperature') ||
      tableName.includes('temp') ||
      displayName.includes('temperature') ||
      description.includes('온도') ||
      description.includes('열')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Temperature',
        name: 'Temperature',
        displayName: '온도',
        labelType: 'Manual',
        description: '설비 온도 데이터',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('torque') ||
      displayName.includes('torque') ||
      description.includes('토크') ||
      description.includes('회전력')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Torque',
        name: 'Torque',
        displayName: '토크',
        labelType: 'Manual',
        description: '토크 (회전력) 데이터',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('downtime') ||
      displayName.includes('downtime') ||
      description.includes('가동 중지') ||
      description.includes('다운타임')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.Downtime',
        name: 'Downtime',
        displayName: '다운타임',
        labelType: 'Manual',
        description: '설비 가동 중지 시간',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (tableName.includes('oee') || displayName.includes('oee') || description.includes('설비 종합 효율')) {
      termTags.push({
        tagFQN: 'Manufacturing.OEE',
        name: 'OEE',
        displayName: 'OEE',
        labelType: 'Manual',
        description: 'OEE (설비 종합 효율)',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('cycle') ||
      displayName.includes('cycle') ||
      description.includes('사이클') ||
      description.includes('생산 시간')
    ) {
      termTags.push({
        tagFQN: 'Manufacturing.CycleTime',
        name: 'CycleTime',
        displayName: '사이클 타임',
        labelType: 'Manual',
        description: '사이클 타임 (생산 시간)',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    // 품질 관련 용어 할당
    if (
      tableName.includes('inspect') ||
      displayName.includes('inspect') ||
      description.includes('검사') ||
      tableName.includes('check')
    ) {
      termTags.push({
        tagFQN: 'Quality.Inspection',
        name: 'Inspection',
        displayName: '검사',
        labelType: 'Manual',
        description: '품질 검사',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('dimension') ||
      displayName.includes('dimension') ||
      description.includes('치수') ||
      tableName.includes('measurement')
    ) {
      termTags.push({
        tagFQN: 'Quality.DimensionalInspection',
        name: 'DimensionalInspection',
        displayName: '치수 검사',
        labelType: 'Manual',
        description: '치수 검사',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('visual') ||
      displayName.includes('visual') ||
      description.includes('외관') ||
      tableName.includes('appearance')
    ) {
      termTags.push({
        tagFQN: 'Quality.VisualInspection',
        name: 'VisualInspection',
        displayName: '외관 검사',
        labelType: 'Manual',
        description: '외관 검사',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('defect') ||
      displayName.includes('defect') ||
      description.includes('불량') ||
      description.includes('결함')
    ) {
      termTags.push({
        tagFQN: 'Quality.Defect',
        name: 'Defect',
        displayName: '불량',
        labelType: 'Manual',
        description: '불량 (결함)',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('yield') ||
      displayName.includes('yield') ||
      description.includes('양품률') ||
      description.includes('합격률')
    ) {
      termTags.push({
        tagFQN: 'Quality.Yield',
        name: 'Yield',
        displayName: '양품률',
        labelType: 'Manual',
        description: '양품률',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('cpk') ||
      displayName.includes('cpk') ||
      description.includes('공정 능력') ||
      tableName.includes('capability')
    ) {
      termTags.push({
        tagFQN: 'Quality.Cpk',
        name: 'Cpk',
        displayName: 'Cpk',
        labelType: 'Manual',
        description: 'Cpk (공정 능력 지수)',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('durability') ||
      displayName.includes('durability') ||
      description.includes('내구') ||
      tableName.includes('endurance')
    ) {
      termTags.push({
        tagFQN: 'Quality.DurabilityTest',
        name: 'DurabilityTest',
        displayName: '내구 시험',
        labelType: 'Manual',
        description: '내구 시험',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('environmental') ||
      displayName.includes('environmental') ||
      description.includes('환경 시험') ||
      tableName.includes('humidity')
    ) {
      termTags.push({
        tagFQN: 'Quality.EnvironmentalTest',
        name: 'EnvironmentalTest',
        displayName: '환경 시험',
        labelType: 'Manual',
        description: '환경 시험',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (
      tableName.includes('functional') ||
      displayName.includes('functional') ||
      description.includes('기능 시험') ||
      tableName.includes('function')
    ) {
      termTags.push({
        tagFQN: 'Quality.FunctionalTest',
        name: 'FunctionalTest',
        displayName: '기능 시험',
        labelType: 'Manual',
        description: '기능 시험',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    // 데이터 관리 관련 용어 할당 (모든 테이블에 일부 적용)
    if (index % 5 === 0) {
      termTags.push({
        tagFQN: 'DataManagement.DataQuality',
        name: 'DataQuality',
        displayName: '데이터 품질',
        labelType: 'Manual',
        description: '데이터 품질 관리',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    if (index % 7 === 0) {
      termTags.push({
        tagFQN: 'DataManagement.Metadata',
        name: 'Metadata',
        displayName: '메타데이터',
        labelType: 'Manual',
        description: '메타데이터',
        source: 'Glossary',
        state: 'Confirmed',
      });
    }

    // Classification 태그: iceberg 원본 tags를 유지하고, 목업에서 부여한 태그(인증·설비 유형·LGES 공정 등)는 같은 FQN이 없을 때만 병합
    const sourceClassificationTags = Array.isArray(source.tags) ? source.tags : [];
    const sourceTagFqnsLc = new Set(sourceClassificationTags.map((t) => t.tagFQN.toLowerCase()));
    const allTags = [...sourceClassificationTags, ...tags.filter((t) => !sourceTagFqnsLc.has(t.tagFQN.toLowerCase()))];

    // 원본 데이터에 terms가 있으면 glossaryTerms로 변환 (Glossary 태그)
    const sourceWithTerms = source as OpenMetadataSourceWithTerms;
    const hasTerms = Array.isArray(sourceWithTerms.terms) && sourceWithTerms.terms.length > 0;
    let glossaryTerms: OpenMetadataTag[] = hasTerms
      ? sourceWithTerms.terms!.map((term) => ({
          tagFQN: term.fullyQualifiedName,
          name: term.name,
          displayName: term.displayName || term.name,
          labelType: 'Manual',
          description: term.displayName || term.name,
          source: 'Glossary' as const,
          state: 'Confirmed',
        }))
      : termTags;

    // 원본 terms가 있어도, 키워드 기반으로 생성한 termTags 중 중복되지 않는 것 추가
    if (hasTerms && termTags.length > 0) {
      const existingTermFQNs = new Set(glossaryTerms.map((t) => t.tagFQN));
      const additionalTerms = termTags.filter((t) => !existingTermFQNs.has(t.tagFQN));
      glossaryTerms = [...glossaryTerms, ...additionalTerms];
    }

    // tags(Classification)와 glossaryTerms(Glossary)를 합침
    const finalTags = [...allTags, ...glossaryTerms];

    return {
      ...hit,
      _source: {
        ...source,
        domains,
        tags: finalTags,
        certification: source.certification,
      },
    };
  });
};

let mockDataStore: OpenMetadataHit[] = createInitialMockData();

const normalizeQuery = (query: string): string => query.trim().toLowerCase();

const matchesQuery = (hit: OpenMetadataHit, query: string): boolean => {
  if (!query) return true;

  const source = hit._source;
  const normalizedQuery = normalizeQuery(query);
  const domainSearchText =
    source.domains?.map((d) => [d.displayName, d.name, d.fullyQualifiedName].filter(Boolean).join(' ')).join(' ') ?? '';
  const tagSearchText =
    source.tags?.map((t) => [t.displayName, t.name, t.tagFQN].filter(Boolean).join(' ')).join(' ') ?? '';

  const searchText = [
    source.name,
    source.displayName,
    source.description || '',
    source.service?.displayName || '',
    source.database?.displayName || '',
    source.databaseSchema?.displayName || '',
    domainSearchText,
    tagSearchText,
  ]
    .join(' ')
    .toLowerCase();

  return searchText.includes(normalizedQuery);
};

const matchesFilters = (hit: OpenMetadataHit, filters: FilterState): boolean => {
  const source = hit._source;

  // quickFilter가 있으면 quickFilter만 사용하고 dataSource는 무시
  const hasQuickFilter = filters.quickFilter && Object.keys(filters.quickFilter).length > 0;

  if (hasQuickFilter) {
    const { serviceType, serviceName, databaseName, schemaName, entityType } = filters.quickFilter!;

    if (serviceType) {
      const sourceTypeNormalized = source.serviceType?.toLowerCase();
      const filterTypeNormalized = serviceType.toLowerCase();
      if (sourceTypeNormalized !== filterTypeNormalized) return false;
    }
    if (serviceName) {
      if (source.service?.displayName !== serviceName) return false;
    }
    if (databaseName && source.database?.displayName !== databaseName) return false;
    if (schemaName && source.databaseSchema?.displayName !== schemaName) return false;
    if (entityType && source.entityType !== entityType) return false;

    // quickFilter 체크 완료 후 dataSource는 건너뜀
  } else if (filters.dataSource && filters.dataSource.length > 0) {
    // dataSource에서 자식(serviceName)이 있으면 부모(serviceType)는 제외
    const serviceNames = filters.dataSource.filter((ds) => ds.includes('-')); // 'gold-iceberg' 같은 serviceName
    const serviceTypes = filters.dataSource.filter((ds) => !ds.includes('-')); // 'iceberg' 같은 serviceType

    // 자식이 있으면 자식만 사용, 없으면 부모 사용
    const effectiveDataSource = serviceNames.length > 0 ? serviceNames : serviceTypes;

    const sourceMatch = effectiveDataSource.some((ds) => {
      const typeMatch = source.serviceType?.toLowerCase() === ds.toLowerCase();
      const nameMatch = source.service?.displayName === ds;
      return typeMatch || nameMatch;
    });
    if (!sourceMatch) return false;
  }

  if (filters.assetType && filters.assetType.length > 0) {
    if (!filters.assetType.includes(source.entityType)) return false;
  }

  if (filters.domain && filters.domain.length > 0) {
    const sourceDomainFqns = source.domains?.map((d) => d.fullyQualifiedName) || [];
    const matchesDomain = filters.domain.some((filterValue) => {
      return sourceDomainFqns.some((domainFqn) => {
        // prefix 필터인 경우 (끝에 .이 있는 경우)
        if (filterValue.endsWith('.')) {
          return domainFqn.startsWith(filterValue);
        }
        // exact match
        return domainFqn === filterValue;
      });
    });
    if (!matchesDomain) return false;
  }

  if (filters.owner && filters.owner.length > 0) {
    const sourceOwners = source.owners?.map((o) => o.displayName) || [];
    if (!filters.owner.some((o) => sourceOwners.includes(o))) return false;
  }

  if (filters.tier && filters.tier.length > 0) {
    if (!source.tier || !filters.tier.includes(source.tier.tagFQN)) return false;
  }

  if (filters.tags && filters.tags.length > 0) {
    const sourceTagsFqnsLc = source.tags?.map((t) => t.tagFQN.toLowerCase()) ?? [];
    if (!filters.tags.some((t) => sourceTagsFqnsLc.includes(t.toLowerCase()))) return false;
  }

  if (filters.certification && filters.certification.length > 0) {
    const sourceCertificationFQN = source.certification?.tagLabel?.tagFQN;
    if (!sourceCertificationFQN) return false;
    const normalizedSourceCert = sourceCertificationFQN.toLowerCase();
    if (!filters.certification.some((c) => c.toLowerCase() === normalizedSourceCert)) return false;
  }

  return true;
};

const sortResults = (results: OpenMetadataHit[], sortBy: string): OpenMetadataHit[] => {
  const sorted = [...results];

  switch (sortBy) {
    case 'name':
      sorted.sort((a, b) => a._source.name.localeCompare(b._source.name));
      break;
    case 'lastUpdated':
      sorted.sort((a, b) => b._source.updatedAt - a._source.updatedAt);
      break;
    case 'relevance':
    default:
      break;
  }

  return sorted;
};

const generateAggregations = (hits: OpenMetadataHit[]): MockOpenMetadataAggregations => {
  const entityTypeCounts = new Map<string, number>();
  const serviceTypeCounts = new Map<string, number>();
  const ownerCounts = new Map<string, number>();
  const tagCounts = new Map<string, number>();
  const tierCounts = new Map<string, number>();
  const serviceCounts = new Map<string, number>();
  const certificationCounts = new Map<string, number>();
  const databaseSchemaCounts = new Map<string, number>();
  const databaseNameCounts = new Map<string, number>();
  const domainCounts = new Map<string, number>();

  hits.forEach((hit) => {
    const source = hit._source;
    entityTypeCounts.set(source.entityType, (entityTypeCounts.get(source.entityType) || 0) + 1);

    if (source.serviceType) {
      serviceTypeCounts.set(source.serviceType, (serviceTypeCounts.get(source.serviceType) || 0) + 1);
    }

    if (source.service?.displayName) {
      serviceCounts.set(source.service.displayName, (serviceCounts.get(source.service.displayName) || 0) + 1);
    }

    if (source.databaseSchema?.displayName) {
      databaseSchemaCounts.set(
        source.databaseSchema.displayName,
        (databaseSchemaCounts.get(source.databaseSchema.displayName) || 0) + 1
      );
    }

    if (source.database?.displayName) {
      databaseNameCounts.set(
        source.database.displayName,
        (databaseNameCounts.get(source.database.displayName) || 0) + 1
      );
    }

    source.owners?.forEach((owner) => {
      ownerCounts.set(owner.displayName, (ownerCounts.get(owner.displayName) || 0) + 1);
    });

    source.tags?.forEach((tag) => {
      tagCounts.set(tag.tagFQN, (tagCounts.get(tag.tagFQN) || 0) + 1);
    });

    if (source.certification?.tagLabel?.tagFQN) {
      const normalizedCertFQN = source.certification.tagLabel.tagFQN.toLowerCase();
      certificationCounts.set(normalizedCertFQN, (certificationCounts.get(normalizedCertFQN) || 0) + 1);
    }

    if (source.tier) {
      tierCounts.set(source.tier.tagFQN, (tierCounts.get(source.tier.tagFQN) || 0) + 1);
    }

    source.domains?.forEach((domain) => {
      domainCounts.set(domain.name, (domainCounts.get(domain.name) || 0) + 1);
    });
  });

  // 실험 데이터 카운트 추가 (통합 검색에서 조회됨)
  entityTypeCounts.set('experiment', 4);
  entityTypeCounts.set('run', 13);
  entityTypeCounts.set('feature_set', 1);
  entityTypeCounts.set('dataset', 2);
  entityTypeCounts.set('artifact', 12);

  return {
    'sterms#entityType': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(entityTypeCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#entityType.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(entityTypeCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#serviceType': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(serviceTypeCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#owners.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(ownerCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#tags.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(tagCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#tier.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(tierCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#service.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(serviceCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#certification.tagLabel.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(certificationCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#databaseSchema.name.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(databaseSchemaCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#databaseSchema.displayName': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(databaseSchemaCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#database.displayName': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(databaseNameCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#database.name.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(databaseNameCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#domain.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(domainCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
    'sterms#domains.name.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: Array.from(domainCounts.entries()).map(([key, doc_count]) => ({ key, doc_count })),
    },
  };
};

export const searchMockData = (
  query: string,
  filters: FilterState,
  from: number,
  size: number,
  sortBy: string = 'relevance'
): OpenMetadataResponse => {
  let results = mockDataStore.filter((hit) => matchesQuery(hit, query) && matchesFilters(hit, filters));

  results = sortResults(results, sortBy);

  const total = results.length;
  const paginatedResults = results.slice(from, from + size);

  const hits: OpenMetadataHit[] = paginatedResults.map((hit, index) => ({
    ...hit,
    _score: 1.0 - (from + index) * 0.01,
  }));

  // 중복 체크
  const hitIds = hits.map((h) => h._id);
  const uniqueHitIds = new Set(hitIds);
  if (hitIds.length !== uniqueHitIds.size) {
    console.error('[searchMockData] 중복된 _id 발견!', {
      from,
      size,
      total: hitIds.length,
      unique: uniqueHitIds.size,
      duplicates: hitIds.filter((id, idx) => hitIds.indexOf(id) !== idx),
    });
  }

  // aggregation 생성 시 필터링된 결과를 기반으로 생성
  const aggregations = from === 0 && size === 0 ? generateAggregations(results) : undefined;

  return {
    took: Math.floor(Math.random() * 50) + 10,
    timed_out: false,
    _shards: {
      total: 1,
      successful: 1,
      skipped: 0,
      failed: 0,
    },
    hits: {
      total: {
        value: total,
        relation: 'eq',
      },
      max_score: hits.length > 0 ? hits[0]._score : 0,
      hits,
    },
    aggregations: aggregations as OpenMetadataResponse['aggregations'],
  };
};

export const getDomainAggregationsMockData = (): DomainAggregationResponse => {
  return {
    aggregations: {
      'sterms#domains.name.keyword': {
        doc_count_error_upper_bound: 0,
        sum_other_doc_count: 0,
        buckets: [
          { key: 'Manufacturing', doc_count: 120 },
          { key: 'Quality', doc_count: 350 },
          { key: 'LGES', doc_count: 72 },
          { key: 'Operations', doc_count: 63 },
        ],
      },
    },
  };
};

export const getInitialAggregationsMockData = (): OpenMetadataResponse => {
  // 사용자가 제공한 실제 aggregations 데이터를 그대로 사용
  const aggregations: MockOpenMetadataAggregations = {
    'sterms#serviceType': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [{ key: 'iceberg', doc_count: 533 }],
    },
    'sterms#databaseSchema.name.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'g_turbo', doc_count: 26 },
        { key: 'g_motor', doc_count: 23 },
        { key: 'g_motor_eocr_idaq_matched', doc_count: 22 },
        { key: 'experiments', doc_count: 10 },
        { key: 'gspower', doc_count: 10 },
        { key: 'local', doc_count: 10 },
        { key: 'g_pdx_v1', doc_count: 6 },
      ],
    },
    'sterms#databaseSchema.displayName': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'g_turbo', doc_count: 26 },
        { key: 'g_motor', doc_count: 23 },
        { key: 'g_motor_eocr_idaq_matched', doc_count: 22 },
        { key: 'experiments', doc_count: 10 },
        { key: 'gspower', doc_count: 10 },
        { key: 'local', doc_count: 10 },
        { key: 'g_pdx_v1', doc_count: 6 },
      ],
    },
    'sterms#entityType': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'table', doc_count: 490 },
        { key: 'glossaryTerm', doc_count: 75 },
        { key: 'tag', doc_count: 51 },
        { key: 'experiment', doc_count: 4 },
        { key: 'run', doc_count: 13 },
        { key: 'feature_set', doc_count: 1 },
        { key: 'dataset', doc_count: 2 },
        { key: 'artifact', doc_count: 12 },
      ],
    },
    'sterms#entityType.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'table', doc_count: 490 },
        { key: 'glossaryTerm', doc_count: 75 },
        { key: 'tag', doc_count: 51 },
      ],
    },
    'sterms#domain.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [],
    },
    'sterms#tier.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'Tier.Tier4', doc_count: 14 },
        { key: 'Tier.Tier2', doc_count: 4 },
        { key: 'Tier.Tier3', doc_count: 4 },
        { key: 'Tier.Tier1', doc_count: 2 },
      ],
    },
    'sterms#service.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'bronze-iceberg', doc_count: 403 },
        { key: 'gold-iceberg', doc_count: 101 },
        { key: 'silver-iceberg', doc_count: 29 },
      ],
    },
    'sterms#database.displayName': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'bronze', doc_count: 402 },
        { key: 'gold', doc_count: 100 },
        { key: 'silver', doc_count: 28 },
      ],
    },
    'sterms#database.name.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'bronze', doc_count: 402 },
        { key: 'gold', doc_count: 100 },
        { key: 'silver', doc_count: 28 },
      ],
    },
    'sterms#certification.tagLabel.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'certification.bronze', doc_count: 12 },
        { key: 'certification.silver', doc_count: 6 },
        { key: 'certification.gold', doc_count: 6 },
      ],
    },
    'sterms#owners.displayName.keyword': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'admin', doc_count: 458 },
        { key: 'product', doc_count: 2 },
        { key: 'ds', doc_count: 1 },
        { key: 'mlops', doc_count: 1 },
      ],
    },
    'sterms#tags.tagFQN': {
      doc_count_error_upper_bound: 0,
      sum_other_doc_count: 0,
      buckets: [
        { key: 'certification.gold', doc_count: 8 },
        { key: 'certification.silver', doc_count: 8 },
        { key: 'certification.bronze', doc_count: 8 },
        { key: 'equipment type.cnc_lathe', doc_count: 12 },
        { key: 'equipment type.furnace', doc_count: 6 },
        { key: 'lges process.cutting', doc_count: 10 },
        { key: 'lges process.stacking', doc_count: 8 },
        { key: 'lges process.cellformation', doc_count: 8 },
        { key: 'lges process.electrodeassembly', doc_count: 7 },
        { key: 'lges process.diagnosticanalysis', doc_count: 14 },
        { key: 'lges process.featurepreparation', doc_count: 9 },
      ],
    },
  };

  return {
    took: Math.floor(Math.random() * 50) + 10,
    timed_out: false,
    _shards: {
      total: 1,
      successful: 1,
      skipped: 0,
      failed: 0,
    },
    hits: {
      total: {
        value: 0,
        relation: 'eq',
      },
      max_score: 0,
      hits: [],
    },
    aggregations: aggregations as OpenMetadataResponse['aggregations'],
  };
};

export const resetSearchMockStore = () => {
  mockDataStore = createInitialMockData();
};
